/**
 * Language detection utilities
 *
 * Handles language detection from various sources:
 * - URL path
 * - Cookies
 * - Accept-Language headers
 */

import { createCookie } from 'react-router';

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@shared-runtime/config/locale';
import { RemixI18Next } from '@shared-web/remix-i18next/server';

/**
 * Language cookie configuration
 */
export const localeCookie = createCookie('lng', {
  path: '/',
  sameSite: 'lax',
  secure: process.env.NODE_ENV === 'production',
  httpOnly: true,
});

/**
 * RemixI18Next instance for server-side language detection
 */
export const i18nextDetector = new RemixI18Next({
  detection: {
    supportedLanguages: [...SUPPORTED_LANGUAGES],
    fallbackLanguage: DEFAULT_LANGUAGE,
    cookie: localeCookie,
    order: ['cookie', 'header'],
  },
  i18next: {
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    ns: ['common', 'nav', 'home'],
  },
});

/**
 * Check if a language code is valid and supported
 */
export function isValidLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

/**
 * Normalize language code to proper case
 * e.g., "en-gb" -> "en-GB", "en-us" -> "en-US"
 */
export function normalizeLanguage(lang: string = ''): SupportedLanguage | null {
  // First check if it's already correct
  if (SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
    return lang as SupportedLanguage;
  }

  // Try to find a case-insensitive match
  const normalizedLang = SUPPORTED_LANGUAGES.find(
    (supportedLang) => supportedLang.toLowerCase() === lang.toLowerCase(),
  );

  return normalizedLang || null;
}

/**
 * Get language from URL path
 *
 * @example
 * getLanguageFromPath('/en-US/trade') // => 'en-US'
 * getLanguageFromPath('/trade') // => null
 */
export function getLanguageFromPath(
  pathname: string,
): SupportedLanguage | null {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && isValidLanguage(firstSegment)) {
    return firstSegment;
  }

  return null;
}

/**
 * Detect language from request
 *
 * Priority:
 * 1. URL path
 * 2. Cookie
 * 3. Accept-Language header
 * 4. Default language
 */
export async function getLocale(request: Request): Promise<SupportedLanguage> {
  const url = new URL(request.url);

  // Try URL path first
  let lng = getLanguageFromPath(url.pathname);

  // Fall back to detector (cookie/header)
  if (!lng || !isValidLanguage(lng)) {
    lng = (await i18nextDetector.getLocale(request)) as SupportedLanguage;
  }

  // Final fallback
  return lng || DEFAULT_LANGUAGE;
}
